import type { User } from "../lib/auth.js";
import { el } from "../lib/dom.js";
import { actionRow, field, messageOf, type Popup, type PopupSpec } from "./ui.js";

export function openDeleteAccount(host: {
  openPopup(spec: PopupSpec): Popup;
  announce(message: string): void;
  remove(): Promise<void>;
}, user: User, returnFocusTo: HTMLElement | (() => HTMLElement | undefined)): Popup {
  let busy = false;
  const email = el("input", { attrs: { type: "email", autocomplete: "off", required: true, maxlength: 254 } });
  const error = el("p", { class: "popup-note" });
  const submit = el("button", { class: "danger", text: "Delete account", attrs: { type: "submit" } });
  const cancel = el("button", { class: "secondary", text: "Cancel", attrs: { type: "button" }, on: { click: () => popup.dismiss() } });
  const form = el("form", { class: "delete-account-form", children: [
    el("p", { text: "Delete your account, all its lists and items, and their history now. There is no undo or recovery period. This browser's saved account data and offline cache will also be cleared." }),
    el("p", { class: "popup-note", text: "Other devices can keep local copies, even after reconnecting. Clear Trayflux's site data on those devices to remove them." }),
    el("p", { class: "popup-note", text: "Existing backups may retain copies until replaced by newer backups, with no fixed deletion date. Abuse-prevention records and sign-in records for former email addresses remain until their normal expiry cleanup." }),
    el("p", { class: "popup-note", text: "Export any lists you want to keep before continuing. Unsynced changes in this browser will also be removed." }),
    field("delete-account-email", `Type ${user.email} to confirm`, email), error,
    actionRow([cancel], submit),
  ], on: { submit: event => {
    event.preventDefault();
    if (busy) return;
    if (email.value.trim().toLowerCase() !== user.email.toLowerCase()) {
      error.textContent = "Enter your current account email."; host.announce(error.textContent); email.focus(); return;
    }
    busy = true; submit.disabled = true; cancel.disabled = true; email.disabled = true;
    void host.remove().catch(reason => {
      busy = false; submit.disabled = false; cancel.disabled = false; email.disabled = false;
      error.textContent = messageOf(reason); host.announce(error.textContent);
    });
  } } });
  const popup = host.openPopup({ title: "Delete account?", body: form, focus: email, returnFocusTo,
    busy: () => busy, guard: { dirty: () => email.value.length > 0, title: "Cancel account deletion?",
      message: "Your account has not been deleted.", keep: "Keep editing", discard: "Cancel deletion" } });
  return popup;
}
